import { API_BASE } from '../config/api';
import { normalizeId } from '../utils/ids';
import { buildUrl, contextAwareHeaders } from './apiClient';

const ONGOING_STATUS_SET = new Set(['live', 'ongoing', 'in_progress', 'in progress']);
const ENTITY_TYPES = ['organization', 'club', 'community'];

const isGaliTournament = (tournamentId) => String(tournamentId || '').toLowerCase() === 'gali';

const createScoringRequestId = () => (
  window.crypto?.randomUUID?.() || `score-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const getGaliApi = async () => import('./galiService');

const normalizeGaliPlayerRef = (player = {}) => {
  if (!player || typeof player !== 'object') return null;
  const participantKey = String(player.participantKey || player.key || player.refKey || player.id || player._id || '').trim();
  if (!participantKey) return null;
  const displayNameSnapshot = String(
    player.displayNameSnapshot ||
    player.displayName ||
    player.name ||
    player.fullName ||
    player.playerName ||
    ''
  ).trim();
  if (!displayNameSnapshot || displayNameSnapshot.toLowerCase() === 'unknown player') return null;
  return {
    ...player,
    id: participantKey,
    playerId: participantKey,
    value: participantKey,
    participantKey,
    name: displayNameSnapshot,
    displayName: displayNameSnapshot,
    displayNameSnapshot,
  };
};

const normalizeGaliRoster = (players = []) => (Array.isArray(players) ? players.map(normalizeGaliPlayerRef).filter(Boolean) : []);

const hasGaliCanonicalRoster = (match = {}) => {
  const config = match?.configuration || {};
  return Boolean(
    Array.isArray(config.playerDirectory) && config.playerDirectory.length > 0 &&
    Array.isArray(config.team1SquadPlayers) && config.team1SquadPlayers.length > 0 &&
    Array.isArray(config.team2SquadPlayers) && config.team2SquadPlayers.length > 0
  );
};

const normalizeGaliMatchForScoring = (payload = {}) => {
  const match = payload.match || payload;
  const configuration = match.configuration || payload.configuration || match.config || {};
  const galiConfig = configuration.gali || match.gali || {};
  const teams = galiConfig.teams || match.teams || {};
  const teamA = teams.teamA || match.teamA || {};
  const teamB = teams.teamB || match.teamB || {};
  const settings = galiConfig.settings || {};
  const score = payload.score || match.score || {};
  const team1 = teamA.name || 'Team A';
  const team2 = teamB.name || 'Team B';
  const team1PlayersData = normalizeGaliRoster(teamA.players || []);
  const team2PlayersData = normalizeGaliRoster(teamB.players || []);
  const directory = normalizeGaliRoster(galiConfig.playerDirectory || payload.playerDirectory || match.playerDirectory || [
    ...team1PlayersData,
    ...team2PlayersData,
  ]);
  return {
    ...match,
    id: String(match.id || match._id || payload.matchId || ''),
    matchType: 'gali',
    team1,
    team2,
    team1PlayersData,
    team2PlayersData,
    team1SquadPlayersData: team1PlayersData,
    team2SquadPlayersData: team2PlayersData,
    configuration: {
      ...configuration,
      matchType: 'gali',
      overs: settings.overs || configuration.overs || match.overs || score.overs || '',
      tossWinner: configuration.tossWinner || team1,
      tossDecision: configuration.tossDecision || (settings.battingFirstTeamKey === 'teamB' ? 'field' : 'bat'),
      team1SquadPlayers: team1PlayersData.map((p) => p.participantKey),
      team2SquadPlayers: team2PlayersData.map((p) => p.participantKey),
      team1PlayersData,
      team2PlayersData,
      team1SquadPlayersData: team1PlayersData,
      team2SquadPlayersData: team2PlayersData,
      playerDirectory: directory,
      gali: { ...galiConfig, playerDirectory: directory },
    },
    playerDirectory: directory,
    score: {
      ...score,
      matchType: 'gali',
      battingTeam: score.battingTeam === 'teamB' ? team2 : score.battingTeam === 'teamA' ? team1 : score.battingTeam,
      bowlingTeam: score.bowlingTeam === 'teamA' ? team1 : score.bowlingTeam === 'teamB' ? team2 : score.bowlingTeam,
    },
  };
};

const normalizeGaliScoreResponse = (payload = {}) => ({
  ...payload,
  match: normalizeGaliMatchForScoring(payload),
});


const handleResponse = async (response, defaultError) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || data.message || defaultError);
    error.status = response.status;
    error.code = data.code;
    error.details = data.details || null;
    throw error;
  }
  return data;
};

const firstArray = (...values) => values.find((value) => Array.isArray(value)) || [];

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const unwrapApiPayload = (data = {}) => {
  let payload = data;

  for (let index = 0; index < 3; index += 1) {
    if (!isPlainObject(payload?.data)) break;
    payload = payload.data;
  }

  return isPlainObject(payload) ? payload : {};
};

const normalizeIdValue = (value) => normalizeId(value) || '';


export const fetchMatchManagerContext = async ({ token, tournamentId }) => {
  const response = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/match-manager-context`, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response, 'Failed to load match manager context.');
};

const normalizeIdArray = (value) => (Array.isArray(value) ? value.map(normalizeIdValue).filter(Boolean) : value);

const normalizeMatchConfigurationShape = (match = {}) => {
  const configurationCandidates = [
    match?.matchConfiguration?.configuration,
    match?.manualSettings?.configuration,
    match?.settings?.configuration,
    match?.format?.configuration,
    match?.config,
    match?.matchConfiguration,
    match?.manualSettings,
    match?.settings,
    match?.format,
    match?.configuration,
  ];

  const configuration = configurationCandidates
    .filter(isPlainObject)
    .reduce((accumulator, current) => ({ ...accumulator, ...current }), {});

  [
    'team1Players',
    'team2Players',
    'team1SquadPlayers',
    'team2SquadPlayers',
    'team1PlayingPlayers',
    'team2PlayingPlayers',
    'team1SubstitutePlayers',
    'team2SubstitutePlayers',
    'team1UnannouncedPlayers',
    'team2UnannouncedPlayers',
    'team1AnnouncedPlayers',
    'team2AnnouncedPlayers',
  ].forEach((key) => {
    configuration[key] = normalizeIdArray(configuration[key]);
  });

  [
    'scorer',
    'team1Captain',
    'team1ViceCaptain',
    'team1WicketKeeper',
    'team2Captain',
    'team2ViceCaptain',
    'team2WicketKeeper',
  ].forEach((key) => {
    if (configuration[key]) configuration[key] = normalizeIdValue(configuration[key]);
  });

  return configuration;
};

const normalizeScheduleMatch = (match = {}) => {
  const rawId = match?.id ?? match?._id ?? match?.matchId ?? null;
  const configuration = normalizeMatchConfigurationShape(match);

  return {
    ...match,
    id: rawId ? normalizeIdValue(rawId) : null,
    team1: firstDefined(match?.team1, match?.manualSettings?.team1, match?.settings?.team1, match?.format?.team1, ''),
    team2: firstDefined(match?.team2, match?.manualSettings?.team2, match?.settings?.team2, match?.format?.team2, ''),
    team1Logo: firstDefined(match?.team1Logo, configuration?.team1Logo, match?.manualSettings?.team1Logo, ''),
    team2Logo: firstDefined(match?.team2Logo, configuration?.team2Logo, match?.manualSettings?.team2Logo, ''),
    configuration,
  };
};

export const extractMatchSchedulePayload = (data = {}) => {
  const payload = unwrapApiPayload(data);
  const matchSchedule = firstDefined(payload?.matchSchedule, payload?.scheduleDoc, payload?.scheduleDocument, null);
  const matches = firstArray(
    payload?.schedule,
    payload?.matches,
    payload?.settings?.schedule,
    payload?.settings?.matches,
    payload?.format?.schedule,
    payload?.format?.matches,
    payload?.manualSettings?.schedule,
    payload?.manualSettings?.matches,
    matchSchedule?.schedule,
    matchSchedule?.matches
  );
  const meta = firstDefined(
    payload?.meta,
    payload?.settings?.meta,
    payload?.format?.meta,
    payload?.manualSettings?.meta,
    matchSchedule?.meta,
    null
  );

  return {
    schedule: matches.map(normalizeScheduleMatch),
    meta,
    matchSchedule,
    tournament: firstDefined(payload?.tournament, payload?.data?.tournament, data?.tournament, null),
  };
};


const emitProfileStatsRefresh = (eventName, detail = {}) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
};

export const scheduleMatches = async ({ token, payload }) => {
  const response = await fetch(`${API_BASE}/api/matches/schedule`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Failed to schedule matches.');
};

export const fetchMatchSchedule = async ({ token, tournamentId }) => {
  const response = await fetch(`${API_BASE}/api/matches/schedule/${tournamentId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return handleResponse(response, 'Failed to load match schedule.');
};

export const saveMatchSchedule = async ({ token, tournamentId, payload }) => {
  const response = await fetch(`${API_BASE}/api/matches/schedule/${tournamentId}/save`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Failed to save match schedule.');
};

export const saveManualMatchSchedule = async ({ token, tournamentId, payload }) => {
  const response = await fetch(`${API_BASE}/api/matches/schedule/${tournamentId}/manual`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Failed to save manual match schedule.');
};


const dynamicScheduleDraftUrl = (tournamentId, draftId = '') => `${API_BASE}/api/tournaments/${encodeURIComponent(tournamentId)}/schedules/dynamic/draft${draftId ? `/${encodeURIComponent(draftId)}` : ''}`;

export const createDynamicScheduleDraft = async ({ token, tournamentId, payload }) => {
  const response = await fetch(dynamicScheduleDraftUrl(tournamentId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Failed to create dynamic schedule draft.');
};

export const fetchDynamicScheduleDraft = async ({ token, tournamentId, draftId }) => {
  const response = await fetch(dynamicScheduleDraftUrl(tournamentId, draftId), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return handleResponse(response, 'Failed to load dynamic schedule draft.');
};

export const updateDynamicScheduleFixture = async ({ token, tournamentId, draftId, fixtureId, payload }) => {
  const response = await fetch(`${dynamicScheduleDraftUrl(tournamentId, draftId)}/fixtures/${encodeURIComponent(fixtureId)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Failed to update dynamic schedule fixture.');
};

export const reorderDynamicScheduleFixtures = async ({ token, tournamentId, draftId, fixtureIds }) => {
  const response = await fetch(`${dynamicScheduleDraftUrl(tournamentId, draftId)}/reorder`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ fixtureIds }),
  });

  return handleResponse(response, 'Failed to reorder dynamic schedule fixtures.');
};


export const updateDynamicSchedulePreferences = async ({ token, tournamentId, draftId, payload }) => {
  const response = await fetch(`${dynamicScheduleDraftUrl(tournamentId, draftId)}/preferences`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'Failed to update dynamic schedule preferences.');
};

export const bulkUpdateDynamicScheduleFixtures = async ({ token, tournamentId, draftId, fixtureIds, patch }) => {
  const response = await fetch(`${dynamicScheduleDraftUrl(tournamentId, draftId)}/bulk`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ fixtureIds, patch }),
  });
  return handleResponse(response, 'Failed to bulk update dynamic schedule fixtures.');
};

export const autoAssignDynamicScheduleFixtures = async ({ token, tournamentId, draftId, preferences }) => {
  const response = await fetch(`${dynamicScheduleDraftUrl(tournamentId, draftId)}/auto-assign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ preferences }),
  });
  return handleResponse(response, 'Failed to auto assign dynamic schedule fixtures.');
};

export const setDynamicScheduleOrderLock = async ({ token, tournamentId, draftId, locked }) => {
  const response = await fetch(`${dynamicScheduleDraftUrl(tournamentId, draftId)}/order-lock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ locked }),
  });
  return handleResponse(response, 'Failed to update fixture order lock.');
};

export const publishDynamicScheduleDraft = async ({ token, tournamentId, draftId }) => {
  const response = await fetch(`${dynamicScheduleDraftUrl(tournamentId, draftId)}/publish`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  return handleResponse(response, 'Failed to publish dynamic schedule draft.');
};

export const updateMatchConfiguration = async ({ token, tournamentId, matchId, payload }) => {
  const response = await fetch(`${API_BASE}/api/matches/${matchId}/configuration?tournamentId=${encodeURIComponent(tournamentId)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Failed to update match configuration.');
};

export const startMatch = async ({ token, tournamentId, matchId, payload }) => {
  if (isGaliTournament(tournamentId)) {
    const { startGaliMatch } = await getGaliApi();
    return normalizeGaliScoreResponse(await startGaliMatch(matchId, payload));
  }
  const response = await fetch(
    `${API_BASE}/api/matches/${matchId}/start?tournamentId=${encodeURIComponent(tournamentId)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  return handleResponse(response, 'Failed to start match.');
};

export const updateMatchScore = async ({ token, tournamentId, matchId, payload }) => {
  if (isGaliTournament(tournamentId)) {
    const { updateGaliMatchScore } = await getGaliApi();
    return normalizeGaliScoreResponse(await updateGaliMatchScore(matchId, payload));
  }
  const response = await fetch(
    `${API_BASE}/api/matches/${matchId}/score?tournamentId=${encodeURIComponent(tournamentId)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  const data = await handleResponse(response, 'Failed to update match score.');
  emitProfileStatsRefresh('scorecard-updated', { tournamentId, matchId, action: payload?.action || null });
  return data;
};

export const endMatch = async ({ token, tournamentId, matchId, scoringStateVersion = 0, requestId }) => {
  if (isGaliTournament(tournamentId)) {
    const { updateGaliMatchScore } = await getGaliApi();
    return normalizeGaliScoreResponse(await updateGaliMatchScore(matchId, { action: 'end_match', requestId: requestId || createScoringRequestId(), expectedStateVersion: scoringStateVersion }));
  }
  const response = await fetch(
    `${API_BASE}/api/matches/${matchId}/score?tournamentId=${encodeURIComponent(tournamentId)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ action: 'end_match', requestId: requestId || createScoringRequestId(), expectedStateVersion: scoringStateVersion }),
    }
  );

  const data = await handleResponse(response, 'Failed to end match.');
  emitProfileStatsRefresh('match-finalized', { tournamentId, matchId });
  emitProfileStatsRefresh('scorecard-updated', { tournamentId, matchId, action: 'end_match' });
  return data;
};

export const startSuperOver = async ({ token, tournamentId, matchId, scoringStateVersion = 0, requestId }) => {
  const response = await fetch(
    `${API_BASE}/api/matches/${matchId}/score?tournamentId=${encodeURIComponent(tournamentId)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ action: 'start_super_over', requestId: requestId || createScoringRequestId(), expectedStateVersion: scoringStateVersion }),
    }
  );

  return handleResponse(response, 'Failed to start super over.');
};

export const fetchMatchScoreboard = async ({ token, tournamentId, matchId }) => {
  const response = await fetch(
    `${API_BASE}/api/matches/schedule/${tournamentId}/matches/${matchId}/scoreboard`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return handleResponse(response, 'Failed to load match scoreboard.');
};

export const fetchPublicMatchScoreboard = async ({ tournamentId, matchId }) => {
  if (isGaliTournament(tournamentId)) {
    const { buildPublicGaliUrl, fetchPublicGaliScoreboard } = await getGaliApi();
    const selectedEndpoint = buildPublicGaliUrl(matchId, '/scoreboard');
    const response = await fetchPublicGaliScoreboard(matchId);
    console.debug('PUBLIC_MATCH_SCOREBOARD_ENDPOINT_DEBUG', {
      tournamentId,
      matchId,
      selectedEndpoint,
      responseStatus: 'ok',
      responseKeys: Object.keys(response || {}),
    });
    return response;
  }
  const selectedEndpoint = `${API_BASE}/api/matches/public/${tournamentId}/matches/${matchId}/scoreboard`;
  const response = await fetch(
    selectedEndpoint,
    { cache: 'no-store' }
  );
  const data = await handleResponse(response, 'Failed to load public match view.');
  console.debug('PUBLIC_MATCH_SCOREBOARD_ENDPOINT_DEBUG', {
    tournamentId,
    matchId,
    selectedEndpoint,
    responseStatus: response.status,
    responseKeys: Object.keys(data || {}),
  });
  return data;
};

export const getPublicMatchWatchLiveRedirectUrl = ({ tournamentId, matchId }) =>
  isGaliTournament(tournamentId)
    ? `${API_BASE}/api/gali/public/matches/${encodeURIComponent(matchId)}`
    : `${API_BASE}/api/matches/public/${tournamentId}/matches/${matchId}/watch-live/redirect`;

export const fetchPublicMatchWatchLive = async ({ tournamentId, matchId }) => {
  const response = await fetch(
    `${API_BASE}/api/matches/public/${tournamentId}/matches/${matchId}/watch-live`,
    { cache: 'no-store' }
  );

  return handleResponse(response, 'Failed to resolve public watch URL.');
};

export const fetchPublicMatchOnlineUsers = async ({ tournamentId, matchId, viewerId }) => {
  if (isGaliTournament(tournamentId)) {
    const { fetchPublicGaliOnlineUsers } = await getGaliApi();
    return fetchPublicGaliOnlineUsers({ publicIdentifier: matchId, viewerId });
  }
  const response = await fetch(
    `${API_BASE}/api/matches/public/${tournamentId}/matches/${matchId}/online-users`,
    {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ viewerId }),
    }
  );

  return handleResponse(response, 'Failed to load online viewers.');
};

export const fetchPublicMatchEngagement = async ({ tournamentId, matchId, viewerId }) => {
  if (isGaliTournament(tournamentId)) {
    const { fetchPublicGaliEngagement } = await getGaliApi();
    return fetchPublicGaliEngagement({ publicIdentifier: matchId, viewerId });
  }
  const suffix = viewerId ? `?viewerId=${encodeURIComponent(viewerId)}` : '';
  const response = await fetch(`${API_BASE}/api/matches/public/${tournamentId}/matches/${matchId}/engagement${suffix}`, { cache: 'no-store' });
  return handleResponse(response, 'Failed to load viewer engagement.');
};

export const sendPublicMatchReaction = async ({ tournamentId, matchId, viewerId, type }) => {
  if (isGaliTournament(tournamentId)) {
    return { ok: true, reaction: { viewerId, type }, unsupported: true };
  }
  const response = await fetch(`${API_BASE}/api/matches/public/${tournamentId}/matches/${matchId}/reactions`, {
    method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ viewerId, type }),
  });
  return handleResponse(response, 'Failed to send reaction.');
};

export const votePublicMatchPoll = async ({ tournamentId, matchId, pollId, optionId, viewerId }) => {
  if (isGaliTournament(tournamentId)) {
    return { ok: true, poll: null, unsupported: true };
  }
  const response = await fetch(`${API_BASE}/api/matches/public/${tournamentId}/matches/${matchId}/polls/${pollId}/vote`, {
    method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ viewerId, optionId }),
  });
  return handleResponse(response, 'Failed to vote in poll.');
};

export const postPublicMatchChat = async ({ tournamentId, matchId, viewerId, displayName, text }) => {
  if (isGaliTournament(tournamentId)) {
    return { ok: true, message: { id: `gali-${Date.now()}`, viewerId, displayName, text, sanitizedText: text }, unsupported: true };
  }
  const response = await fetch(`${API_BASE}/api/matches/public/${tournamentId}/matches/${matchId}/chat`, {
    method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ viewerId, displayName, text }),
  });
  return handleResponse(response, 'Failed to send chat message.');
};

export const followPublicMatch = async ({ tournamentId, matchId, viewerId, following }) => {
  if (isGaliTournament(tournamentId)) {
    return { ok: true, follow: { viewerId, following: Boolean(following) }, unsupported: true };
  }
  const action = following ? 'follow' : 'unfollow';
  const response = await fetch(`${API_BASE}/api/matches/public/${tournamentId}/matches/${matchId}/${action}`, {
    method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ viewerId }),
  });
  return handleResponse(response, 'Failed to update follow status.');
};

export const fetchLocalStreamPreview = async () => {
  const response = await fetch(`${API_BASE}/api/stream/local-preview`);
  return handleResponse(response, 'Failed to load local preview configuration.');
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.matches)) return value.matches;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const normalizeEntityType = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw.startsWith('org')) return 'organization';
  if (raw.startsWith('club')) return 'club';
  if (raw.startsWith('comm')) return 'community';
  return 'individual';
};

const isOngoingStatus = (status) => ONGOING_STATUS_SET.has(String(status || '').trim().toLowerCase());

const toTime = (value) => {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const requestJson = async ({ token, path, headers = {}, signal }) => {
  const response = await fetch(buildUrl(path), {
    signal,
    headers: contextAwareHeaders({
      Authorization: `Bearer ${token}`,
      ...headers,
    }),
  });

  return handleResponse(response, 'Failed to load ongoing matches.');
};

const normalizeMatch = (match, defaults = {}) => {
  const matchId = match?.matchId || match?.id || match?._id || match?.match?.matchId || match?.match?.id || match?.match?._id;
  const tournamentId =
    match?.tournamentId
    || match?.tournament?.id
    || match?.tournament?._id
    || (typeof match?.tournament === 'string' ? match.tournament : null)
    || defaults.tournamentId
    || null;

  if (!matchId) return null;

  const teamAName =
    match?.teamA?.name
    || match?.teamAName
    || match?.team1
    || match?.teamOne
    || match?.homeTeam?.name
    || 'Team A';

  const teamBName =
    match?.teamB?.name
    || match?.teamBName
    || match?.team2
    || match?.teamTwo
    || match?.awayTeam?.name
    || 'Team B';

  const entityType = normalizeEntityType(match?.entityType || match?.scope || defaults.entityType || 'individual');

  return {
    matchId: String(matchId),
    id: String(matchId),
    tournamentId: tournamentId ? String(tournamentId) : null,
    tournament: {
      id: tournamentId ? String(tournamentId) : null,
      name: match?.tournament?.name || match?.tournamentName || defaults.tournamentName || '',
    },
    tournamentName: match?.tournamentName || match?.tournament?.name || defaults.tournamentName || '',
    title:
      match?.title
      || match?.name
      || match?.matchName
      || `${match?.tournament?.name || defaults.tournamentName || 'Match'} • ${teamAName} vs ${teamBName}`,
    status: String(match?.status || match?.score?.status || defaults.status || 'live'),
    entityType,
    entityId: String(match?.entityId || match?.organizationId || match?.clubId || match?.communityId || defaults.entityId || ''),
    entityName:
      match?.entityName
      || match?.organizationName
      || match?.clubName
      || match?.communityName
      || defaults.entityName
      || (entityType === 'individual' ? 'Individual' : 'Unknown'),
    teamAName,
    teamBName,
    scoreSummary: match?.scoreSummary || match?.score?.summary || match?.scoreLine || '',
    overs: match?.overs || match?.score?.overs || '',
    startTime: match?.startTime || match?.date || match?.scheduledAt || match?.createdAt || '',
    updatedAt: match?.updatedAt || match?.lastEventAt || match?.modifiedAt || '',
    venue: match?.venue || match?.location || '',
  };
};

const dedupeMatches = (matches) => {
  const byId = new Map();
  matches.forEach((match) => {
    if (!match?.matchId) return;
    const existing = byId.get(match.matchId);
    if (!existing || toTime(match.updatedAt || match.startTime) > toTime(existing.updatedAt || existing.startTime)) {
      byId.set(match.matchId, match);
    }
  });
  return [...byId.values()];
};

const sortOngoingMatches = (matches) =>
  [...matches].sort(
    (first, second) =>
      toTime(second.updatedAt || second.startTime) - toTime(first.updatedAt || first.startTime)
  );

const fetchEntities = async ({ token, entityType, signal }) => {
  const payload = await requestJson({ token, path: `/api/${entityType}s/my`, signal });
  return asArray(payload)
    .map((item) => ({
      id: String(item?.id || item?._id || item?.[`${entityType}Id`] || ''),
      name: item?.name || item?.title || item?.[`${entityType}Name`] || entityType,
      entityType,
    }))
    .filter((entity) => entity.id);
};

const fetchScopedOngoingMatches = async ({ token, entityType, entityId, entityName, signal }) => {
  const payload = await requestJson({
    token,
    path: `/api/${entityType}s/${entityId}/matches?status=ongoing`,
    signal,
    headers: {
      'X-Entity-Type': entityType,
      'X-Entity-Id': String(entityId),
    },
  });

  return asArray(payload)
    .map((match) => normalizeMatch(match, { entityType, entityId, entityName }))
    .filter(Boolean)
    .filter((match) => isOngoingStatus(match.status));
};

const fetchIndividualOngoingMatches = async ({ token, signal }) => {
  const payload = await requestJson({ token, path: '/api/matches?scope=individual&status=ongoing', signal });
  return asArray(payload)
    .map((match) => normalizeMatch(match, { entityType: 'individual', entityName: 'Individual' }))
    .filter(Boolean)
    .filter((match) => isOngoingStatus(match.status));
};

const shouldFallbackToScoped = (error) => {
  const message = String(error?.message || '').toLowerCase();
  return (
    Number(error?.status) === 404
    || Number(error?.status) === 501
    || message.includes('active entity context required')
    || message.includes('not found')
    || message.includes('request failed (404)')
  );
};


export const getGlobalLiveMatches = async ({ token, signal, page = 1, limit = 20 } = {}) => {
  if (!token) {
    return {
      items: [],
      page: 1,
      limit,
      total: 0,
      totalPages: 0,
    };
  }

  const safePage = Number.isFinite(Number(page)) && Number(page) > 0 ? Number(page) : 1;
  const safeLimit = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : 20;
  const payload = await requestJson({
    token,
    path: `/api/matches/live?page=${encodeURIComponent(safePage)}&limit=${encodeURIComponent(safeLimit)}`,
    signal,
  });

  const normalizedItems = asArray(payload)
    .map((match) => normalizeMatch(match))
    .filter(Boolean)
    .filter((match) => isOngoingStatus(match.status));

  return {
    items: sortOngoingMatches(dedupeMatches(normalizedItems)),
    page: Number(payload?.page) || safePage,
    limit: Number(payload?.limit) || safeLimit,
    total: Number(payload?.total) || 0,
    totalPages: Number(payload?.totalPages) || 0,
  };
};
export const getOngoingMatches = async ({ token, signal, scope = 'all' } = {}) => {
  if (!token) {
    return [];
  }

  try {
    const payload = await requestJson({ token, path: `/api/matches/ongoing?scope=${encodeURIComponent(scope || 'all')}`, signal });
    const normalized = asArray(payload)
      .map((match) => normalizeMatch(match))
      .filter(Boolean)
      .filter((match) => isOngoingStatus(match.status));

    return sortOngoingMatches(dedupeMatches(normalized));
  } catch (error) {
    if (!shouldFallbackToScoped(error)) {
      throw error;
    }
  }

  const entityResults = await Promise.allSettled(
    ENTITY_TYPES.map((entityType) => fetchEntities({ token, entityType, signal }))
  );
  const entities = entityResults.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));

  const scopedRequests = entities.map((entity) =>
    fetchScopedOngoingMatches({
      token,
      entityType: entity.entityType,
      entityId: entity.id,
      entityName: entity.name,
      signal,
    })
  );

  scopedRequests.push(fetchIndividualOngoingMatches({ token, signal }));

  const scopedResults = await Promise.allSettled(scopedRequests);
  const matches = scopedResults.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
  return sortOngoingMatches(dedupeMatches(matches));
};



export const getIndividualAssignedMatches = async ({ token, signal } = {}) => {
  if (!token) {
    return { live: [], upcoming: [], past: [] };
  }

  const payload = await requestJson({
    token,
    path: '/api/matches/my',
    signal,
  });

  const normalizeBucket = (items, status) =>
    asArray(items)
      .map((match) => normalizeMatch(match, { entityType: 'individual', entityName: 'Individual', status }))
      .filter(Boolean);

  return {
    live: sortOngoingMatches(dedupeMatches(normalizeBucket(payload?.live, 'ongoing'))),
    upcoming: normalizeBucket(payload?.upcoming, 'upcoming').sort(
      (first, second) => toTime(first.startTime) - toTime(second.startTime)
    ),
    past: dedupeMatches(normalizeBucket(payload?.past, 'completed')).sort(
      (first, second) => toTime(second.updatedAt || second.startTime) - toTime(first.updatedAt || first.startTime)
    ),
  };
};



export const getStreamingDashboard = async ({
  token,
  signal,
  limit = 100,
  search = '',
  status = 'all',
  source = 'all',
  unhealthy = false,
  tournamentId,
} = {}) => {
  const query = new URLSearchParams({
    limit: String(limit),
    search: String(search || ''),
    status: String(status || 'all'),
    source: String(source || 'all'),
    unhealthy: unhealthy ? '1' : '0',
  });
  if (tournamentId) {
    query.set('tournamentId', String(tournamentId));
  }
  const response = await fetch(`${API_BASE}/api/matches/streaming-dashboard?${query.toString()}`, {
    signal,
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response, 'Failed to load streaming dashboard.');
};

export const fetchMatchStreamDetails = async ({ token, matchId, tournamentId }) => {
  const query = tournamentId ? `?tournamentId=${encodeURIComponent(tournamentId)}` : '';
  const response = await fetch(`${API_BASE}/api/matches/${matchId}/stream-details${query}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return handleResponse(response, 'Failed to load stream details.');
};

export const generateMatchYouTubeStream = async ({ token, matchId, tournamentId }) => {
  const query = tournamentId ? `?tournamentId=${encodeURIComponent(tournamentId)}` : '';
  const response = await fetch(`${API_BASE}/api/matches/${matchId}/youtube/generate${query}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return handleResponse(response, 'Failed to generate YouTube stream details.');
};


export const takeoverStreamControl = async ({ token, matchId, tournamentId, forceTakeover = false, streamType = 'match' }) => {
  const isLiveAuction = streamType === 'live_auction' || streamType === 'auction';
  const isOnlineAuction = streamType === 'online_auction';
  const url = isLiveAuction
    ? `${API_BASE}/api/tournaments/${tournamentId}/live-auction/streaming/takeover`
    : isOnlineAuction
      ? `${API_BASE}/api/tournaments/${tournamentId}/online-auction/streaming/takeover`
      : `${API_BASE}/api/matches/${matchId}/streaming/takeover`;
  const response = await fetch(url, {
    method: 'POST',
    headers: contextAwareHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({ tournamentId, forceTakeover }),
  });
  return handleResponse(response, 'Failed to take over stream control.');
};

export const fetchMatchStreamEvents = async ({ token, matchId, tournamentId, streamType = 'match' }) => {
  const isLiveAuction = streamType === 'live_auction' || streamType === 'auction';
  const isOnlineAuction = streamType === 'online_auction';
  const url = isLiveAuction
    ? `${API_BASE}/api/tournaments/${tournamentId}/live-auction/streaming/events`
    : isOnlineAuction
      ? `${API_BASE}/api/tournaments/${tournamentId}/online-auction/streaming/events`
      : `${API_BASE}/api/matches/${matchId}/streaming/events${tournamentId ? `?tournamentId=${encodeURIComponent(tournamentId)}` : ''}`;
  const response = await fetch(url, {
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response, 'Failed to load stream events.');
};




export const getUpcomingMatches = async ({ token, signal, page = 1, limit = 20 } = {}) => {
  if (!token) {
    return {
      items: [],
      page: 1,
      limit,
      total: 0,
      totalPages: 0,
    };
  }

  const safePage = Number.isFinite(Number(page)) && Number(page) > 0 ? Number(page) : 1;
  const safeLimit = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : 20;
  const payload = await requestJson({
    token,
    path: `/api/matches/upcoming?page=${encodeURIComponent(safePage)}&limit=${encodeURIComponent(safeLimit)}`,
    signal,
  });

  const normalizedItems = asArray(payload)
    .map((match) => normalizeMatch(match, { status: 'upcoming' }))
    .filter(Boolean);

  return {
    items: [...normalizedItems].sort((first, second) => toTime(first.startTime) - toTime(second.startTime)),
    page: Number(payload?.page) || safePage,
    limit: Number(payload?.limit) || safeLimit,
    total: Number(payload?.total) || normalizedItems.length,
    totalPages: Number(payload?.totalPages) || (normalizedItems.length ? 1 : 0),
  };
};

export const getCompletedMatches = async ({ token, signal, page = 1, limit = 20 } = {}) => {
  if (!token) {
    return {
      items: [],
      page: 1,
      limit,
      total: 0,
      totalPages: 0,
    };
  }

  const safePage = Number.isFinite(Number(page)) && Number(page) > 0 ? Number(page) : 1;
  const safeLimit = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : 20;
  const payload = await requestJson({
    token,
    path: `/api/matches/completed?page=${encodeURIComponent(safePage)}&limit=${encodeURIComponent(safeLimit)}`,
    signal,
  });

  const normalizedItems = asArray(payload)
    .map((match) => normalizeMatch(match, { status: 'completed' }))
    .filter(Boolean);

  return {
    items: [...normalizedItems].sort((first, second) => toTime(second.updatedAt || second.startTime) - toTime(first.updatedAt || first.startTime)),
    page: Number(payload?.page) || safePage,
    limit: Number(payload?.limit) || safeLimit,
    total: Number(payload?.total) || normalizedItems.length,
    totalPages: Number(payload?.totalPages) || (normalizedItems.length ? 1 : 0),
  };
};

export const fetchMatchById = async ({ token, matchId, tournamentId }) => {
  if (isGaliTournament(tournamentId)) {
    const { fetchGaliMatch, fetchGaliScoreboard } = await getGaliApi();
    const [matchPayload, scorePayload] = await Promise.all([fetchGaliMatch(matchId), fetchGaliScoreboard(matchId).catch(() => ({}))]);
    const normalized = normalizeGaliMatchForScoring({ ...matchPayload, score: scorePayload?.score || matchPayload?.score });
    const team1 = normalized.configuration?.team1PlayersData || [];
    const team2 = normalized.configuration?.team2PlayersData || [];
    const directory = normalized.configuration?.playerDirectory || [];
    return { match: normalized, team1, team2, playerDirectory: directory };
  }
  const query = tournamentId ? `?tournamentId=${encodeURIComponent(tournamentId)}` : '';
  const response = await fetch(`${API_BASE}/api/matches/${matchId}${query}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await handleResponse(response, 'Failed to load match details.');
  if (data?.match) {
    data.match.id = String(data.match.id || data.match._id || matchId);
  }
  return data;
};

export const fetchMatchConfiguration = async ({ token, matchId, tournamentId }) => {
  const query = tournamentId ? `?tournamentId=${encodeURIComponent(tournamentId)}` : '';
  const response = await fetch(`${API_BASE}/api/matches/${matchId}/configuration${query}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return handleResponse(response, 'Failed to load match configuration.');
};

export const fetchMatchScore = async ({ token, matchId, tournamentId }) => {
  if (isGaliTournament(tournamentId)) {
    const { fetchGaliScoreboard } = await getGaliApi();
    return normalizeGaliScoreResponse(await fetchGaliScoreboard(matchId));
  }
  const query = tournamentId ? `?tournamentId=${encodeURIComponent(tournamentId)}` : '';
  const response = await fetch(`${API_BASE}/api/matches/${matchId}/score${query}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return handleResponse(response, 'Failed to load match score.');
};


export const fetchRelayStatus = async ({ token, matchId, tournamentId }) => {
  const query = tournamentId ? `?tournamentId=${encodeURIComponent(tournamentId)}` : '';
  const response = await fetch(`${API_BASE}/api/matches/${matchId}/relay/status${query}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return handleResponse(response, 'Failed to load relay status.');
};

export const startRelay = async ({ token, matchId, tournamentId }) => {
  const payload = tournamentId ? { tournamentId } : {};
  const response = await fetch(`${API_BASE}/api/matches/${matchId}/relay/start`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'Failed to start relay.');
};

export const stopRelay = async ({ token, matchId, tournamentId }) => {
  const response = await fetch(`${API_BASE}/api/matches/${matchId}/relay/stop`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tournamentId }),
  });
  return handleResponse(response, 'Failed to stop relay.');
};


export const fetchMatchLiveStatus = async ({ token, matchId, tournamentId }) => {
  const query = tournamentId ? `?tournamentId=${encodeURIComponent(tournamentId)}` : '';
  const response = await fetch(`${API_BASE}/api/matches/${matchId}/live/status${query}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return handleResponse(response, 'Failed to load live status.');
};

export const fetchPublicMatchWatch = async ({ matchId }) => {
  const response = await fetch(`${API_BASE}/api/matches/${matchId}/watch`);
  return handleResponse(response, 'Failed to load public watch details.');
};

export const generateTeamJoinLink = async ({ token, tournamentId, matchId, teamKey, expiresInDays, expiresAt, maxJoins, joinStatus }) => {
  const response = await fetch(`${API_BASE}/api/matches/${encodeURIComponent(tournamentId)}/${encodeURIComponent(matchId)}/team-join-link`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ teamKey, expiresInDays, expiresAt, maxJoins, joinStatus }),
  });
  if (!response.ok) throw new Error('Unable to generate team join link');
  return response.json();
};
export const getPublicTeamJoinInvite = async (token) => {
  const response = await fetch(`${API_BASE}/api/public/team-join/${encodeURIComponent(token)}`);
  if (!response.ok) throw new Error('Invalid join link');
  return response.json();
};
export const requestPublicTeamJoinOtp = async ({ inviteToken, email, mobile }) => {
  const response = await fetch(`${API_BASE}/api/public/team-join/${encodeURIComponent(inviteToken)}/request-otp`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, mobile }) });
  if (!response.ok) throw new Error('Unable to send OTP');
  return response.json();
};
export const verifyPublicTeamJoinOtp = async ({ inviteToken, email, mobile, otp, name }) => {
  const response = await fetch(`${API_BASE}/api/public/team-join/${encodeURIComponent(inviteToken)}/verify`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, mobile, otp, name }) });
  if (!response.ok) throw new Error('OTP verification failed');
  return response.json();
};
export const completeTeamJoinInvite = async ({ token, inviteToken }) => {
  const response = await fetch(`${API_BASE}/api/matches/team-join/${encodeURIComponent(inviteToken)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Unable to complete team join');
  return response.json();
};

export const getBroadcastHistory = async ({ token, broadcastType = 'match', entityId, entityType = '', signal } = {}) => {
  if (!entityId) return { sessions: [], replays: [], replaySummary: {} };
  const query = entityType ? `?entityType=${encodeURIComponent(entityType)}` : '';
  const response = await fetch(`${API_BASE}/api/broadcasts/${encodeURIComponent(broadcastType)}/${encodeURIComponent(entityId)}/history${query}`, {
    signal,
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response, 'Failed to load broadcast history.');
};

export const setPrimaryBroadcastReplay = async ({ token, sessionId } = {}) => {
  const response = await fetch(`${API_BASE}/api/broadcasts/sessions/${encodeURIComponent(sessionId)}/primary`, {
    method: 'PATCH',
    headers: contextAwareHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response, 'Failed to set primary replay.');
};

export const __matchServiceGaliInternals = { normalizeGaliMatchForScoring, normalizeGaliRoster, normalizeGaliPlayerRef, hasGaliCanonicalRoster };
